/* eslint-disable eqeqeq */
import { useCallback, useMemo, useState } from 'react';

export type Order = 'asc' | 'desc';

export interface UseTableControlsOptions<T> {
  rows: T[];
  defaultOrderBy?: keyof T | string;
  defaultOrder?: Order;
  defaultPage?: number;
  defaultRowsPerPage?: number;
}

export function useTableControls<T = any>({
  rows,
  defaultOrderBy,
  defaultOrder = 'asc',
  defaultPage = 0,
  defaultRowsPerPage = 100,
}: UseTableControlsOptions<T>) {
  const [orderBy, setOrderBy] = useState<string | undefined>(
    (defaultOrderBy as string) || undefined
  );
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [page, setPage] = useState<number>(defaultPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage);
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());

  const toggleExpand = useCallback((id: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleRequestSort = useCallback(
    (property: string) => {
      if (orderBy !== property) {
        setOrder('asc');
        setOrderBy(property);
      } else if (order === 'asc') {
        setOrder('desc');
      } else {
        setOrderBy(undefined);
      }
      setPage(0);
    },
    [order, orderBy]
  );

  const handleChangePage = useCallback((_e: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
    []
  );

  const sortedRows = useMemo(() => {
    if (!orderBy) return rows;
    const copy = [...rows];
    copy.sort((a: any, b: any) => {
      const av = a?.[orderBy as any];
      const bv = b?.[orderBy as any];
      // Always push null/undefined to the end regardless of sort order
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return order === 'asc' ? av - bv : bv - av;
      }
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        // false < true for asc
        const aNum = av ? 1 : 0;
        const bNum = bv ? 1 : 0;
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // Fallback to string compare
      return order === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [order, orderBy, rows]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  return {
    page,
    rowsPerPage,
    order,
    orderBy,
    expanded,
    toggleExpand,
    setPage,
    setRowsPerPage,
    setOrder,
    setOrderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    sortedRows,
    pagedRows,
  } as const;
}
